import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { FiHeart, FiCheck, FiLayers, FiCreditCard } from 'react-icons/fi';
import api from '../../services/api';
import { useServerConfig } from '../../hooks/useServerConfig';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';

const Container = styled.div`
  min-height: 100vh;
  background: var(--background-secondary);
  padding: 2rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 3rem;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 1.5rem;
  }
`;

const DonationCard = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1rem;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  }
`;


const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  letter-spacing: -0.03em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h4`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.01em;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1.5px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.9375rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.1);
  }

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }
  
  /* Ensure readOnly inputs still display text properly */
  &[readonly] {
    background: var(--background-primary);
    color: var(--text-primary);
    cursor: default;
  }
  
  /* Ensure disabled inputs still display text */
  &:disabled {
    opacity: 1;
    background: var(--background-primary);
    color: var(--text-primary);
    cursor: default;
  }
`;

const PaymentElementContainer = styled.div`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  transition: var(--transition);

  &:focus-within {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }

  /* Force all text to be black */
  * {
    color: #000000 !important;
  }

  /* Override Stripe PaymentElement text colors */
  .Label,
  label,
  span,
  div,
  p,
  button,
  a {
    color: #000000 !important;
  }

  /* Override green security message */
  [class*="Link"],
  [class*="link"],
  [class*="secure"],
  [class*="Secure"] {
    color: #000000 !important;
  }

  /* Override payment method labels (Card, US bank account) */
  [class*="Tab"],
  [class*="tab"],
  [class*="PaymentMethod"],
  [class*="payment-method"],
  [class*="TabList"],
  [class*="tab-list"],
  [class*="TabPanel"],
  [class*="tab-panel"] {
    color: #000000 !important;
    
    * {
      color: #000000 !important;
    }
    
    svg,
    path {
      fill: #000000 !important;
      stroke: #000000 !important;
    }
  }

  /* More specific overrides for Stripe's internal structure */
  div[class*="StripeElement"],
  div[class*="stripe-element"],
  div[class*="PaymentElement"],
  div[class*="payment-element"],
  iframe[class*="Stripe"],
  iframe[class*="stripe"] {
    color: #000000 !important;
    
    * {
      color: #000000 !important;
    }
  }

  /* Override any text that uses primary color or beige/gold colors */
  [style*="color: var(--primary-color)"],
  [style*="color:#D8B889"],
  [style*="color:#B8956A"],
  [style*="color:rgb(216, 184, 137)"],
  [style*="color:rgba(216, 184, 137"],
  [style*="color:rgb(184, 149, 106)"],
  [style*="color:rgba(184, 149, 106"] {
    color: #000000 !important;
  }

  /* Target all possible Stripe class names */
  [class*="Stripe"] *,
  [class*="stripe"] *,
  [id*="Stripe"] *,
  [id*="stripe"] * {
    color: #000000 !important;
  }

  /* Override SVG icons that might be colored */
  svg {
    fill: #000000 !important;
    color: #000000 !important;
    
    path,
    circle,
    rect,
    line {
      fill: #000000 !important;
      stroke: #000000 !important;
    }
  }

  /* Override input text colors */
  input,
  select {
    color: #000000 !important;
    
    &::placeholder {
      color: #999999 !important; /* Keep placeholders grey for readability */
    }
  }

  /* Customize Stripe PaymentElement labels for US bank account */
  label[for*="email"],
  .Label[for*="email"],
  label:has(input[name*="email"]),
  label:has(input[type="email"]) {
    font-size: 0 !important;
    &::after {
      content: "Your Email Address:";
      font-size: 0.875rem !important;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0.025em;
      color: #000000 !important;
      display: block;
      margin-bottom: 0.5rem;
    }
  }

  label[for*="name"],
  .Label[for*="name"],
  label:has(input[name*="name"]),
  label:has(input[placeholder*="name" i]) {
    font-size: 0 !important;
    &::after {
      content: "Your Full Name:";
      font-size: 0.875rem !important;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0.025em;
      color: #000000 !important;
      display: block;
      margin-bottom: 0.5rem;
    }
  }

  /* Hide Stripe PaymentElement card field labels (placeholders serve as labels) */
  /* Hide labels for card number, expiry, CVC, country, and postal code fields */
  .Label {
    /* Hide labels that contain uppercase text (Stripe's card field labels) */
    &[for*="cardNumber"],
    &[for*="cardExpiry"],
    &[for*="cardCvc"],
    &[for*="country"],
    &[for*="postal"],
    &[for*="zip"] {
      display: none !important;
    }
  }

  label {
    &[for*="cardNumber"],
    &[for*="cardExpiry"],
    &[for*="cardCvc"],
    &[for*="country"],
    &[for*="postal"],
    &[for*="zip"] {
      display: none !important;
    }
  }

  /* Hide labels that are siblings or children of card field containers */
  div[id*="cardNumber"] ~ .Label,
  div[id*="cardNumber"] + .Label,
  div[id*="cardExpiry"] ~ .Label,
  div[id*="cardExpiry"] + .Label,
  div[id*="cardCvc"] ~ .Label,
  div[id*="cardCvc"] + .Label,
  div[id*="country"] ~ .Label,
  div[id*="country"] + .Label,
  div[id*="postal"] ~ .Label,
  div[id*="postal"] + .Label,
  div[id*="zip"] ~ .Label,
  div[id*="zip"] + .Label {
    display: none !important;
  }

  /* Hide labels using :has selector for modern browsers */
  label:has(+ div[id*="cardNumber"]),
  label:has(+ div[id*="cardExpiry"]),
  label:has(+ div[id*="cardCvc"]),
  label:has(+ div[id*="country"]),
  label:has(+ div[id*="postal"]),
  label:has(+ div[id*="zip"]) {
    display: none !important;
  }

  /* General rule: Hide all uppercase labels (Stripe's default card field label style) */
  /* This targets labels with uppercase text-transform which Stripe uses for card fields */
  .Label[style*="text-transform: uppercase"],
  .Label[style*="TEXT-TRANSFORM: UPPERCASE"] {
    display: none !important;
  }

  /* Hide labels based on Stripe's field structure - but preserve email/name labels */
  /* Only hide labels in card-related fields, not in email/name fields */
  div[class*="Field"]:not(:has(input[type="email"])):not(:has(input[name*="email"])):not(:has(input[name*="name"])) > .Label:not([for*="email"]):not([for*="name"]),
  div[class*="Field"]:not(:has(input[type="email"])):not(:has(input[name*="email"])):not(:has(input[name*="name"])) > label:not([for*="email"]):not([for*="name"]) {
    display: none !important;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  box-shadow: 0 2px 8px rgba(184, 149, 106, 0.2);
  
  &:hover:not(:disabled) {
    background-color: var(--primary-hover);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(184, 149, 106, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(184, 149, 106, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(252, 129, 129, 0.1);
  color: var(--error-color);
  padding: 1rem;
  border-radius: 0;
  border: 1px solid var(--error-color);
  margin-bottom: 1rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background: rgba(104, 211, 145, 0.1);
  color: var(--success-color);
  padding: 1rem;
  border-radius: 0;
  border: 1px solid var(--success-color);
  margin-bottom: 1rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SummaryBox = styled.div`
  background: var(--background-secondary);
  border: 1.5px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-top: 1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-family: 'Inter', sans-serif;
`;

const SummaryLabel = styled.span`
  color: #000000 !important;
  font-size: 0.875rem;
`;

const SummaryValue = styled.span`
  color: #000000 !important;
  font-weight: 600;
  font-size: 0.875rem;
`;

const SummaryTotal = styled(SummaryRow)`
  margin-top: 0.5rem;
  padding-top: 1rem;
  font-size: 1.1rem;
  
  ${SummaryLabel}, ${SummaryValue} {
    font-size: 1.1rem;
    font-weight: 700;
    color: #000000 !important;
  }
`;


const QuickShortcuts = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: nowrap;
  justify-content: space-between;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 0.375rem;
    flex-wrap: wrap;
  }
`;

const ShortcutButton = styled.button`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 0.5rem;
  border: 1.5px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  
  /* Default state - use darker text for better contrast */
  color: var(--text-primary) !important;

  &:hover:not(:disabled):not(.selected) {
    border-color: var(--primary-color);
    background: var(--primary-color);
    color: var(--text-inverse) !important;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(184, 149, 106, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: var(--text-muted) !important;
    border-color: var(--border-color);
    background: var(--background-secondary);
    transform: none;
    box-shadow: none;
    /* Ensure color is visible even when disabled */
    -webkit-text-fill-color: var(--text-muted) !important;
  }

  &.selected {
    border-color: var(--primary-color);
    background: var(--primary-color);
    color: var(--text-inverse) !important;
    box-shadow: 0 2px 8px rgba(184, 149, 106, 0.25);
  }
  
  @media (max-width: 768px) {
    flex: 0 1 auto;
    min-width: calc(33.333% - 0.5rem);
    padding: 0.625rem 0.5rem;
    font-size: 0.8125rem;
  }
`;


const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border: 1.5px solid var(--border-color);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  
  &:hover {
    border-color: var(--primary-color);
    background-color: rgba(216, 184, 137, 0.05);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }
  
  input[type="radio"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #000000;
    
    &:checked {
      accent-color: #000000;
      background-color: #000000;
      border-color: #000000;
    }
    
    &::before {
      background-color: #000000;
    }
  }
  
  &.selected {
    border-color: var(--primary-color);
    background-color: rgba(216, 184, 137, 0.1);
    box-shadow: 0 2px 6px rgba(184, 149, 106, 0.15);
  }
`;

const RadioLabel = styled.span`
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-primary);
`;

function DonationFormContent({ 
  onClientSecretChange, 
  clientSecretFromParent, 
  preservedOneTimeAmount, 
  setPreservedOneTimeAmount, 
  preservedDonationType, 
  setPreservedDonationType,
  preservedMonthlyAmount: preservedMonthlyAmountFromParent,
  setPreservedMonthlyAmount: setPreservedMonthlyAmountFromParent,
  preservedNumberOfPayments: preservedNumberOfPaymentsFromParent,
  setPreservedNumberOfPayments: setPreservedNumberOfPaymentsFromParent,
  preservedIsForever: preservedIsForeverFromParent,
  setPreservedIsForever: setPreservedIsForeverFromParent,
  preservedSubscriptionData: preservedSubscriptionDataFromParent,
  setPreservedSubscriptionData: setPreservedSubscriptionDataFromParent
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // These hooks will return null if Elements is not rendered yet (which is fine)
  const stripe = useStripe();
  const elements = useElements();

  // Use preserved donation type if available (to prevent reset on remount), otherwise default to 'one-time'
  const [donationType, setDonationType] = useState(preservedDonationType || 'one-time'); // 'one-time' or 'monthly'
  
  // Update donationType when preservedDonationType changes (from parent)
  useEffect(() => {
    if (preservedDonationType && preservedDonationType !== donationType) {
      setDonationType(preservedDonationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preservedDonationType]);
  const [oneTimeAmount, setOneTimeAmount] = useState('');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [numberOfPayments, setNumberOfPayments] = useState('');
  const [isForever, setIsForever] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [donationFor, setDonationFor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Best Practice: Store paymentIntentId to allow reusing PaymentIntent if checkout is interrupted
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  // Store subscription/subscription schedule data for monthly donations
  // Use preserved subscription data from parent if available (survives remounts)
  const [subscriptionData, setSubscriptionData] = useState(preservedSubscriptionDataFromParent || null);
  
  // Sync subscriptionData when preserved value changes
  useEffect(() => {
    if (preservedSubscriptionDataFromParent && !subscriptionData) {
      setSubscriptionData(preservedSubscriptionDataFromParent);
    }
  }, [preservedSubscriptionDataFromParent, subscriptionData]);
  
  
  // Use preserved values from parent (they survive remounts)
  // These values are stored in the parent component and persist across Elements remounts
  const preservedMonthlyAmount = preservedMonthlyAmountFromParent;
  const preservedNumberOfPayments = preservedNumberOfPaymentsFromParent;
  const preservedIsForever = preservedIsForeverFromParent;
  
  // Helper functions to set preserved values in parent
  // These update the parent component's state, which survives remounts
  const setPreservedMonthlyAmount = useCallback((value) => {
    setPreservedMonthlyAmountFromParent(value);
  }, [setPreservedMonthlyAmountFromParent]);
  
  const setPreservedNumberOfPayments = useCallback((value) => {
    setPreservedNumberOfPaymentsFromParent(value);
  }, [setPreservedNumberOfPaymentsFromParent]);
  
  const setPreservedIsForever = useCallback((value) => {
    setPreservedIsForeverFromParent(value);
  }, [setPreservedIsForeverFromParent]);
  

  // CRITICAL: Sync form state with preserved values when they become available
  // This ensures inputs display values even if component remounts
  useEffect(() => {
    if (preservedMonthlyAmount && (!monthlyAmount || monthlyAmount === '')) {
      setMonthlyAmount(preservedMonthlyAmount.toString());
    }
  }, [preservedMonthlyAmount, monthlyAmount]);

  useEffect(() => {
    if (preservedNumberOfPayments && preservedNumberOfPayments !== 'forever' && (!numberOfPayments || numberOfPayments === '')) {
      setNumberOfPayments(preservedNumberOfPayments.toString());
    }
  }, [preservedNumberOfPayments, numberOfPayments]);

  useEffect(() => {
    if (preservedIsForever && !isForever) {
      setIsForever(true);
    }
  }, [preservedIsForever, isForever]);

  useEffect(() => {
    // Calculate total when amounts change
  }, [oneTimeAmount, monthlyAmount, numberOfPayments]);

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateTotal = () => {
    if (donationType === 'one-time') {
      const amount = preservedOneTimeAmount || parseFloat(oneTimeAmount) || 0;
      return amount.toFixed(2);
    } else {
      // Monthly donation - show total over all payments
      // Use preserved values if available (they persist even if form values are cleared)
      const amount = preservedMonthlyAmount || parseFloat(monthlyAmount) || 0;
      const forever = preservedIsForever || isForever;
      
      if (forever) {
        // For forever subscriptions, we can't calculate a total
        return null;
      }
      
      const payments = preservedNumberOfPayments || numberOfPayments;
      if (amount && payments) {
        const numPayments = typeof payments === 'number' ? payments : parseInt(payments);
        return (amount * (numPayments || 0)).toFixed(2);
      }
      return amount.toFixed(2);
    }
  };

  const [clientSecret, setClientSecret] = useState(clientSecretFromParent || null);
  const [showPaymentElement, setShowPaymentElement] = useState(!!clientSecretFromParent);
  const [showPaymentForm, setShowPaymentForm] = useState(!!clientSecretFromParent);
  const [creatingPaymentIntent, setCreatingPaymentIntent] = useState(false);
  const paymentIntentCreatedRef = useRef(!!clientSecretFromParent);

  // Update local state when parent provides clientSecret
  useEffect(() => {
    if (clientSecretFromParent) {
      // For one-time donations, always show payment element
      if (donationType === 'one-time') {
        setClientSecret(clientSecretFromParent);
        setShowPaymentElement(true);
      } 
      // For monthly donations (including forever), show payment element if we have clientSecret
      else if (donationType === 'monthly') {
        setClientSecret(clientSecretFromParent);
        setShowPaymentElement(true);
      }
    } else if (donationType !== 'one-time' && donationType !== 'monthly' && (clientSecret || clientSecretFromParent)) {
      // Clear if we have a clientSecret but switched away from one-time or monthly
      setClientSecret(null);
      setShowPaymentElement(false);
      if (onClientSecretChange) {
        onClientSecretChange(null);
      }
    }
  }, [clientSecretFromParent, donationType, clientSecret, onClientSecretChange]);

  // Also show payment element when we have clientSecret from parent for one-time donations
  useEffect(() => {
    if (clientSecretFromParent && donationType === 'one-time' && !showPaymentElement) {
      setShowPaymentElement(true);
    }
  }, [clientSecretFromParent, donationType, showPaymentElement]);

  // REMOVED: Auto-create payment intent - now only created when user clicks "Proceed to Payment"

  // REMOVED: Auto-create subscription/payment intent - now only created when user clicks "Proceed to Payment"

  // Notify parent when clientSecret changes
  useEffect(() => {
    if (onClientSecretChange && clientSecret) {
      onClientSecretChange(clientSecret);
    }
  }, [clientSecret, onClientSecretChange]);

  // Show payment form when clientSecret becomes available (after "Proceed to Payment" click)
  useEffect(() => {
    if (clientSecret && paymentIntentCreatedRef.current && !showPaymentForm) {
      logger.debug('Client secret became available, showing payment form');
      setShowPaymentForm(true);
      setShowPaymentElement(true);
    }
  }, [clientSecret, showPaymentForm]);

  // Handler for "Proceed to Payment" button
  const handleProceedToPayment = async () => {
    if (creatingPaymentIntent || paymentIntentCreatedRef.current) {
      return; // Already creating or created
    }
    
    setCreatingPaymentIntent(true);
    setError('');
    
    try {
      logger.info('User clicked Proceed to Payment - creating donation payment intent');
      
      if (donationType === 'one-time') {
        // Create one-time donation payment intent
        const currentAmount = oneTimeAmount ? parseFloat(oneTimeAmount) : (preservedOneTimeAmount || 0);
        
        if (!currentAmount || currentAmount < 0.01 || isNaN(currentAmount)) {
          setError('Please enter a valid donation amount.');
          setCreatingPaymentIntent(false);
          return;
        }
        
        const requestBody = {
          oneTimeAmount: currentAmount,
          donationType: 'one-time',
        };
        
        if (firstName && firstName.trim()) {
          requestBody.firstName = firstName.trim();
        }
        if (lastName && lastName.trim()) {
          requestBody.lastName = lastName.trim();
        }
        if (email && email.trim()) {
          requestBody.email = email.trim();
        }
        if (phone && phone.trim()) {
          requestBody.phone = phone.trim();
        }
        if (!user && donationFor && donationFor.trim()) {
          requestBody.donationFor = donationFor.trim();
        }
        
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify(requestBody)
        });

        if (response.success && response.oneTimePayment) {
          setPreservedOneTimeAmount(currentAmount);
          const newClientSecret = response.oneTimePayment.clientSecret;
          
          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }
          
          setClientSecret(newClientSecret);
          setPaymentIntentId(response.oneTimePayment.paymentIntentId);
          setPreservedDonationType('one-time');
          paymentIntentCreatedRef.current = true;
          
          // Wait a moment for clientSecret to be set
          await new Promise(resolve => setTimeout(resolve, 200));
          
          if (newClientSecret) {
            setShowPaymentForm(true);
            setShowPaymentElement(true);
            logger.info('One-time donation payment intent created, showing payment form');
          } else {
            setError('Failed to initialize payment. Please try again.');
            logger.error('Payment intent not created after Proceed to Payment click');
          }
        } else {
          setError(response.message || response.error || 'Failed to create payment intent. Please try again.');
          logger.error('Failed to create one-time donation payment intent', { response });
        }
      } else if (donationType === 'monthly') {
        // Create monthly donation payment intent/subscription
        const currentMonthlyAmount = preservedMonthlyAmount || parseFloat(monthlyAmount);
        const currentIsForever = preservedIsForever || isForever;
        const currentNumberOfPayments = preservedNumberOfPayments || numberOfPayments;
        
        if (!currentMonthlyAmount || currentMonthlyAmount < 0.01 || isNaN(currentMonthlyAmount)) {
          setError('Please enter a valid monthly donation amount.');
          setCreatingPaymentIntent(false);
          return;
        }
        
        if (!currentIsForever && (!currentNumberOfPayments || currentNumberOfPayments < 1 || currentNumberOfPayments > 120)) {
          setError('Please select a valid number of payments (1-120) or select Unlimited.');
          setCreatingPaymentIntent(false);
          return;
        }
        
        const requestBody = {
          monthlyAmount: currentMonthlyAmount,
          numberOfPayments: currentIsForever ? 'forever' : currentNumberOfPayments,
          donationType: 'monthly',
        };
        
        if (firstName && firstName.trim()) {
          requestBody.firstName = firstName.trim();
        }
        if (lastName && lastName.trim()) {
          requestBody.lastName = lastName.trim();
        }
        if (email && email.trim()) {
          requestBody.email = email.trim();
        }
        if (phone && phone.trim()) {
          requestBody.phone = phone.trim();
        }
        if (!user && donationFor && donationFor.trim()) {
          requestBody.donationFor = donationFor.trim();
        }
        
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify(requestBody)
        });

        const responseClientSecret = response?.subscription?.clientSecret || response?.clientSecret;
        
        if (response.success && responseClientSecret) {
          setPreservedMonthlyAmount(currentMonthlyAmount);
          setPreservedIsForever(currentIsForever);
          setPreservedNumberOfPayments(currentIsForever ? 'forever' : currentNumberOfPayments);
          const newClientSecret = responseClientSecret;
          
          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }
          
          setClientSecret(newClientSecret);
          setSubscriptionData(response.subscription || { clientSecret: newClientSecret });
          setPreservedSubscriptionDataFromParent(response.subscription || { clientSecret: newClientSecret });
          setPreservedDonationType('monthly');
          paymentIntentCreatedRef.current = true;
          
          // Wait a moment for clientSecret to be set
          await new Promise(resolve => setTimeout(resolve, 200));
          
          if (newClientSecret) {
            setShowPaymentForm(true);
            setShowPaymentElement(true);
            logger.info('Monthly donation payment intent created, showing payment form');
          } else {
            setError('Failed to initialize payment. Please try again.');
            logger.error('Payment intent not created after Proceed to Payment click');
          }
        } else {
          setError(response.message || response.error || 'Failed to create payment intent. Please try again.');
          logger.error('Failed to create monthly donation payment intent', { response });
        }
      }
    } catch (error) {
      logger.error('Error creating donation payment intent on Proceed to Payment', { error });
      setError('Failed to initialize payment. Please try again.');
    } finally {
      setCreatingPaymentIntent(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError('');
    setSuccess('');

    // Validate inputs based on donation type
    if (donationType === 'one-time') {
      const amountToCheck = preservedOneTimeAmount || parseFloat(oneTimeAmount);
      if (!amountToCheck || amountToCheck <= 0) {
        setError('Please enter a valid one-time donation amount.');
        return;
      }
    } else {
      // Monthly donation - ALWAYS use preserved values if payment element is already shown
      // Otherwise use form values, but also preserve them immediately
      const monthlyAmt = (showPaymentElement && preservedMonthlyAmount) 
        ? preservedMonthlyAmount 
        : (preservedMonthlyAmount || parseFloat(monthlyAmount));
      const forever = (showPaymentElement && preservedIsForever !== null)
        ? preservedIsForever
        : (preservedIsForever || isForever);
      const payments = (showPaymentElement && preservedNumberOfPayments)
        ? preservedNumberOfPayments
        : (preservedNumberOfPayments || numberOfPayments);
      
      // If we have form values but not preserved values, preserve them NOW
      if (!preservedMonthlyAmount && monthlyAmount) {
        setPreservedMonthlyAmount(parseFloat(monthlyAmount));
      }
      if (!preservedNumberOfPayments && numberOfPayments && !isForever) {
        setPreservedNumberOfPayments(parseInt(numberOfPayments));
      }
      if (preservedIsForever === false && isForever) {
        setPreservedIsForever(true);
      }
      
      // Monthly donation - amount is required
      if (!monthlyAmt || monthlyAmt <= 0 || isNaN(monthlyAmt)) {
        setError('Monthly donation amount is required. Please enter a valid amount.');
        return;
      }
      // Number of payments or unlimited option is required
      if (!forever && (!payments || payments === 'forever' || (typeof payments === 'string' && payments.trim() === '') || (typeof payments === 'number' ? payments < 1 : parseInt(payments) < 1) || (typeof payments === 'number' ? payments > 120 : parseInt(payments) > 120))) {
        setError('Number of monthly payments is required. Please enter a number between 1 and 120, or select "Unlimited".');
        return;
      }
    }

    if (!user && (!firstName || !lastName)) {
      setError('Please fill in all required fields (First Name, Last Name).');
      return;
    }

    if (!stripe) {
      setError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    // If we have a one-time payment and no clientSecret yet, create payment intent first
    // Check both oneTimeAmount and preservedOneTimeAmount
    const amountToUse = preservedOneTimeAmount || parseFloat(oneTimeAmount);
    if (donationType === 'one-time' && amountToUse && !clientSecret && !clientSecretFromParent) {
      setLoading(true);
      try {
        const amount = amountToUse;
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify({
            oneTimeAmount: amount,
            firstName,
            lastName,
            email,
            ...(phone && phone.trim() && { phone: phone.trim() }),
            // Best Practice: Include existing PaymentIntent ID to reuse if checkout was interrupted
            ...(paymentIntentId && { existingPaymentIntentId: paymentIntentId }),
          })
        });

        if (response.success && response.oneTimePayment) {
          setPreservedOneTimeAmount(amount); // Preserve the amount
          // Best Practice: Store clientSecret securely - never log it or expose in URLs
          const newClientSecret = response.oneTimePayment.clientSecret;
          
          // CRITICAL: Update parent FIRST via callback, then update local state
          // This ensures Elements remounts with the new clientSecret before we show PaymentElement
          if (onClientSecretChange) {
            onClientSecretChange(newClientSecret);
          }
          
          setClientSecret(newClientSecret);
          
          // Best Practice: Store paymentIntentId to allow reusing PaymentIntent if checkout is interrupted
          setPaymentIntentId(response.oneTimePayment.paymentIntentId);
          // Preserve donation type to prevent reset on Elements remount
          setPreservedDonationType('one-time');
          setShowPaymentElement(true);
          setLoading(false);
          return; // Wait for user to complete payment
        } else {
          setError(response.message || 'Failed to create payment intent. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Failed to create payment. Please try again.');
        setLoading(false);
        return;
      }
    }

    // If we have clientSecret and payment element, confirm the payment
    if (clientSecret && stripe && elements) {
      setLoading(true);
      try {
        // First, submit the elements to validate the form
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message || 'Please check your payment information.');
          setLoading(false);
          return;
        }

        // Check if this is a one-time donation or subscription
        // Use preserved subscription data if local state was lost on remount
        // Also check preserved donation type to determine payment type
        const activeSubscriptionData = subscriptionData || preservedSubscriptionDataFromParent;
        const isSubscriptionPayment = !!activeSubscriptionData || preservedDonationType === 'monthly';
        
        if (isSubscriptionPayment) {
          // For subscriptions, ALWAYS use preserved values - they should be set when subscription was created
          // CRITICAL: Use preserved values from parent (they survive remounts)
          const monthlyAmt = preservedMonthlyAmount || parseFloat(monthlyAmount);
          const numPayments = preservedNumberOfPayments || numberOfPayments;
          const forever = preservedIsForever || isForever;
          
          // Validate monthly donation values before confirming payment
          if (!monthlyAmt || monthlyAmt <= 0 || isNaN(monthlyAmt)) {
            setError('Please enter a valid monthly donation amount.');
            setLoading(false);
            return;
          }
          
          if (!forever && (!numPayments || numPayments === 'forever' || (typeof numPayments === 'string' && numPayments.trim() === '') || (typeof numPayments === 'number' ? numPayments < 1 : parseInt(numPayments) < 1))) {
            setError('Please enter a valid number of monthly payments or select "Unlimited".');
            setLoading(false);
            return;
          }
          
          // For subscriptions, confirm the payment intent attached to the subscription invoice
          const { error: confirmError } = await stripe.confirmPayment({
            elements,
            clientSecret: clientSecret,
            confirmParams: {
              return_url: `${window.location.origin}/portal/donation/success`,
            },
            redirect: 'if_required',
          });

          if (confirmError) {
            setError(confirmError.message || 'Payment failed. Please try again.');
            setLoading(false);
            return;
          }

          // Retrieve payment intent with expanded charges to get receipt URL
          const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret, {
            expand: ['charges']
          });

          // Get payment intent ID from the retrieved payment intent (more reliable than parsing client secret)
          const paymentIntentId = paymentIntent?.id;
          
          if (!paymentIntentId) {
            setError('Could not retrieve payment intent ID. Please contact support.');
            setLoading(false);
            return;
          }

          // Get receipt URL from payment intent
          const receiptUrl = paymentIntent?.charges?.data?.[0]?.receipt_url || null;

          // Prepare donation data based on subscription type
          // Use preserved values (already validated above)
          // CRITICAL: Use preserved subscription data which survives remounts
          const activeSubscriptionData = subscriptionData || preservedSubscriptionDataFromParent;
          
          const donationData = {
            donationType: 'monthly',
            monthlyAmount: monthlyAmt,
            isForever: forever,
            user: {
              firstName: firstName,
              lastName: lastName,
              email: email,
              first_name: firstName,
              last_name: lastName
            },
            receiptUrl: receiptUrl,
            paymentIntentId: paymentIntentId
          };

          // Use preserved subscription data to get subscription IDs
          if (forever && activeSubscriptionData?.subscriptionId) {
            donationData.numberOfPayments = 'forever';
            donationData.totalAmount = null;
            donationData.subscriptionId = activeSubscriptionData.subscriptionId;
          } else if (activeSubscriptionData?.subscriptionScheduleId) {
            const paymentsNum = typeof numPayments === 'number' ? numPayments : parseInt(numPayments);
            donationData.numberOfPayments = paymentsNum;
            donationData.totalAmount = monthlyAmt * paymentsNum;
            donationData.subscriptionScheduleId = activeSubscriptionData.subscriptionScheduleId;
            donationData.subscriptionId = activeSubscriptionData.subscriptionId;
          } else if (activeSubscriptionData?.subscriptionId) {
            // Fallback: if we have subscriptionId but no schedule, it's a forever subscription
            const paymentsNum = typeof numPayments === 'number' ? numPayments : parseInt(numPayments);
            donationData.numberOfPayments = forever ? 'forever' : paymentsNum;
            donationData.totalAmount = forever ? null : (monthlyAmt * paymentsNum);
            donationData.subscriptionId = activeSubscriptionData.subscriptionId;
          }

          // Navigate to confirmation page (no /portal prefix needed - basename is set in Router)
          navigate('/donation/success', {
            state: { donationData }
          });
        } else {
          // One-time donation flow
          // Validate that we still have the amount (use preserved amount if available)
          const amountToUse = preservedOneTimeAmount || parseFloat(oneTimeAmount);
          if (!amountToUse || amountToUse <= 0) {
            setError('Please enter a valid donation amount.');
            setLoading(false);
            return;
          }

          // Confirm the payment
          // Include receipt_email in confirmParams to ensure email is captured
          const confirmParams = {
            return_url: `${window.location.origin}/portal/donation/success`,
          };
          
          // Add receipt_email if email is available (helps Stripe capture it)
          if (email && email.trim()) {
            confirmParams.receipt_email = email.trim();
          }
          
          const { error: confirmError, paymentIntent: confirmedPaymentIntent } = await stripe.confirmPayment({
            elements,
            clientSecret: clientSecret,
            confirmParams: confirmParams,
            redirect: 'if_required',
          });

          if (confirmError) {
            setError(confirmError.message || 'Payment failed. Please try again.');
            setLoading(false);
            return;
          }

          // Get payment intent ID from the confirmation response (most reliable)
          // If not available, retrieve it separately
          let paymentIntent = confirmedPaymentIntent;
          let paymentIntentId = confirmedPaymentIntent?.id;
          
          if (!paymentIntentId) {
            // Fallback: Retrieve payment intent with expanded charges to get receipt URL
            logger.debug('Payment intent not in confirmPayment response, retrieving separately...');
            try {
              const result = await stripe.retrievePaymentIntent(clientSecret, {
                expand: ['charges']
              });
              paymentIntent = result.paymentIntent;
              paymentIntentId = paymentIntent?.id;
            } catch (retrieveError) {
              logger.error('Error retrieving payment intent', { error: retrieveError });
              // Try to extract from client secret as last resort
              const fallbackId = clientSecret.split('_secret_')[0];
              if (fallbackId && fallbackId.startsWith('pi_')) {
                paymentIntentId = fallbackId;
                logger.debug('Using fallback payment intent ID from client secret', { fallbackId });
              } else {
                setError('Could not retrieve payment intent ID. Please contact support.');
                setLoading(false);
                return;
              }
            }
          }
          
          if (!paymentIntentId) {
            setError('Could not retrieve payment intent ID. Please contact support.');
            setLoading(false);
            return;
          }

          logger.debug('Sending payment intent ID to backend', { 
            paymentIntentId, 
            status: paymentIntent?.status, 
            amount: paymentIntent?.amount 
          });

          // Confirm donation on backend (works for both authenticated and guest users)
          // Add a small delay to ensure Stripe has processed the payment intent
          await new Promise(resolve => setTimeout(resolve, 1000));

          try {
            // Use publicRequest for guest donations, request for authenticated users
            if (user) {
              await api.request('/payments/confirm-donation', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  paymentIntentId: paymentIntentId
                })
              });
            } else {
              await api.publicRequest('/payments/confirm-donation', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  paymentIntentId: paymentIntentId
                })
              });
            }
            logger.debug('Backend confirmation successful');
          } catch (confirmErr) {
            // Continue anyway as payment was successful
            logger.warn('Backend confirmation failed, but payment was successful', { error: confirmErr });
            logger.warn('Error details', {
              message: confirmErr.message,
              response: confirmErr.response
            });
          }

          // Get receipt URL from payment intent
          const receiptUrl = paymentIntent?.charges?.data?.[0]?.receipt_url || null;
          
          // Navigate to confirmation page with donation data (use correct path without /portal prefix due to basename)
          navigate('/donation/success', {
            state: {
              donationData: {
                donationType: 'one-time',
                oneTimeAmount: amountToUse,
                totalAmount: amountToUse,
                user: {
                  firstName: firstName,
                  lastName: lastName,
                  email: email,
                  first_name: firstName,
                  last_name: lastName
                },
                receiptUrl: receiptUrl,
                paymentIntentId: paymentIntentId
              }
            }
          });
        }
      } catch (err) {
          setError(err.message || 'An error occurred during payment confirmation.');
          setLoading(false);
      }
    } else if (donationType === 'monthly') {
      // Monthly donation - use PaymentMethod approach for subscriptions
      // Use preserved values if available (in case form was already submitted once)
      const monthlyAmt = preservedMonthlyAmount || parseFloat(monthlyAmount);
      const forever = preservedIsForever || isForever;
      const payments = preservedNumberOfPayments || numberOfPayments;
      
      // Validate before making API call
      if (!monthlyAmt || monthlyAmt <= 0) {
        setError('Monthly donation amount is required. Please enter a valid amount.');
        setLoading(false);
        return;
      }
      
      if (!forever && (!payments || (typeof payments === 'string' && payments.trim() === '') || (typeof payments === 'number' ? payments < 1 : parseInt(payments) < 1))) {
        setError('Number of monthly payments is required. Please enter a positive number, or select "Unlimited".');
        setLoading(false);
        return;
      }
      
      // For fixed-term monthly donations (not forever), use the new PaymentMethod approach
      if (!forever && payments && parseInt(payments) >= 1) {
        // NEW APPROACH: Use PaymentMethod + Subscription Schedule
        if (!elements) {
          setError('Payment form is not ready. Please wait a moment and try again.');
          setLoading(false);
          return;
        }
        
        setLoading(true);
        try {
          // Step 1: Submit elements to validate the form (REQUIRED before createPaymentMethod)
          const { error: submitError } = await elements.submit();
          if (submitError) {
            setError(submitError.message || 'Please check your payment information.');
            setLoading(false);
            return;
          }

          // Step 2: Create PaymentMethod from PaymentElement (after submit)
          const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
            elements,
            params: {
              billing_details: {
                email: email,
                name: `${firstName} ${lastName}`.trim()
              }
            }
          });

          if (pmError) {
            setError(pmError.message || 'Failed to collect payment method. Please check your payment information.');
            setLoading(false);
            return;
          }

          if (!paymentMethod) {
            setError('Failed to create payment method. Please try again.');
            setLoading(false);
            return;
          }

          // Generate idempotency key (donationRequestId)
          const donationRequestId = `donation-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
          

          // Step 2: Create subscription schedule using new route
          const scheduleRequestBody = {
            email,
            name: `${firstName} ${lastName}`.trim(),
            amountUsd: monthlyAmt,
            months: parseInt(payments),
            payment_method_id: paymentMethod.id,
            donationRequestId
          };
          
          // Include donationFor for public users (replaces "Faith Insight Donation")
          if (!user && donationFor && donationFor.trim()) {
            scheduleRequestBody.donationFor = donationFor.trim();
          }
          
          const response = await api.publicRequest('/payments/create-donation-schedule', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
            },
            body: JSON.stringify(scheduleRequestBody)
          });

          if (response.success) {
            // Success! Navigate to confirmation page
            const donationData = {
              donationType: 'monthly',
              monthlyAmount: monthlyAmt,
              numberOfPayments: parseInt(payments),
              totalAmount: monthlyAmt * parseInt(payments),
              subscriptionScheduleId: response.scheduleId,
              subscriptionId: response.subscriptionId,
              status: response.status,
              user: {
                firstName,
                lastName,
                email,
                first_name: firstName,
                last_name: lastName
              }
            };

            // Navigate to confirmation page
            navigate('/donation/success', {
              state: { donationData }
            });
          } else {
            setError(response.message || response.error || 'Failed to create donation schedule. Please try again.');
            setLoading(false);
          }
        } catch (err) {
          setError(err.message || 'Failed to create donation. Please try again.');
          setLoading(false);
        }
        return;
      }
      
      // For "forever" subscriptions or payments > 36, use the old PaymentIntent approach
      setLoading(true);
      try {
        // Ensure numberOfPayments is sent as a number (not string) when not forever
        const numberOfPaymentsToSend = forever ? 'forever' : (typeof payments === 'number' ? payments : parseInt(payments, 10));
        
        const foreverRequestBody = {
          monthlyAmount: monthlyAmt,
          numberOfPayments: numberOfPaymentsToSend,
          firstName,
          lastName,
          email,
          ...(phone && phone.trim() && { phone: phone.trim() }),
        };
        
        // Include donationFor for public users (replaces "Faith Insight Donation")
        if (!user && donationFor && donationFor.trim()) {
          foreverRequestBody.donationFor = donationFor.trim();
        }
        
        const response = await api.publicRequest('/payments/create-donation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
          },
          body: JSON.stringify(foreverRequestBody)
        });

        if (response.success) {
          // Check if we have a clientSecret for payment collection (subscription/subscription schedule)
          const clientSecret = response.subscription?.clientSecret || response.subscriptionSchedule?.clientSecret;
          
          if (clientSecret) {
            // IMPORTANT: Preserve monthly donation values BEFORE showing payment element
            // This ensures the values are available when the form re-renders
            // Use the values we already calculated (may include preserved values)
            const preservedAmount = monthlyAmt;
            const preservedPayments = forever ? 'forever' : (typeof payments === 'number' ? payments : parseInt(payments));
            
            // Set preserved values first - CRITICAL: These must be set before showing payment element
            setPreservedMonthlyAmount(preservedAmount);
            setPreservedNumberOfPayments(preservedPayments);
            setPreservedIsForever(forever);
            setPreservedOneTimeAmount(null); // Clear one-time amount
            // Preserve donation type to prevent reset on Elements remount
            setPreservedDonationType('monthly');
            
            // Also ensure form state values are set (they should already be set, but ensure they're not empty)
            if (!monthlyAmount || monthlyAmount === '' || parseFloat(monthlyAmount) !== preservedAmount) {
              setMonthlyAmount(preservedAmount.toString());
            }
            if (!numberOfPayments || numberOfPayments === '' || (numberOfPayments !== preservedPayments.toString() && numberOfPayments !== preservedPayments)) {
              if (!forever && preservedPayments !== 'forever') {
                setNumberOfPayments(preservedPayments.toString());
              }
            }
            
            // Now store subscription data in BOTH local and parent state (survives remounts)
            const subscriptionDataToStore = response.subscription || response.subscriptionSchedule;
            setSubscriptionData(subscriptionDataToStore);
            setPreservedSubscriptionDataFromParent(subscriptionDataToStore);
            
            setClientSecret(clientSecret);
            setShowPaymentElement(true);
            
            setLoading(false);
            return; // Wait for user to complete payment
          } else {
            // No client secret - subscription might have been created without payment (shouldn't happen)
            setError('Failed to create payment method collection. Please try again.');
            setLoading(false);
            return;
          }
        } else {
          setError(response.message || 'Failed to create donation. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'An error occurred. Please try again.');
        setLoading(false);
      }
    }
  };

  const totalAmount = calculateTotal();

  return (
    <DonationCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Header>
        <Title>
          <FiHeart /> Make a Donation
        </Title>
        <Subtitle>Support our mission with a one-time or recurring donation</Subtitle>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && (
        <SuccessMessage>
          <FiCheck /> {success}
        </SuccessMessage>
      )}

      <Form onSubmit={handleSubmit}>
        <FormSection>
          <SectionTitle>
            <FiLayers /> Donation Type
          </SectionTitle>
          
          <RadioGroup>
            <RadioOption 
              className={donationType === 'one-time' ? 'selected' : ''}
              onClick={() => {
                // Allow switching donation type even when payment element is shown
                setDonationType('one-time');
                setPreservedDonationType('one-time');
                setMonthlyAmount('');
                setNumberOfPayments('');
                setPreservedMonthlyAmount(null);
                setPreservedNumberOfPayments(null);
                setPreservedIsForever(false);
                // Reset payment element when switching types
                setClientSecret(null);
                setShowPaymentElement(false);
                setShowPaymentForm(false);
                setSubscriptionData(null);
                setPaymentIntentId(null);
                paymentIntentCreatedRef.current = false;
                setError('');
                // Clear parent's clientSecret if callback exists
                if (onClientSecretChange) {
                  onClientSecretChange(null);
                }
              }}
            >
              <input
                type="radio"
                name="donationType"
                value="one-time"
                checked={donationType === 'one-time'}
                onChange={() => {
                  // Allow switching donation type even when payment element is shown
                  setDonationType('one-time');
                  setPreservedDonationType('one-time');
                  setMonthlyAmount('');
                  setNumberOfPayments('');
                  setPreservedMonthlyAmount(null);
                  setPreservedNumberOfPayments(null);
                  setPreservedIsForever(false);
                  // Reset payment element when switching types
                  setClientSecret(null);
                  setShowPaymentElement(false);
                  setShowPaymentForm(false);
                  setSubscriptionData(null);
                  setPaymentIntentId(null);
                  paymentIntentCreatedRef.current = false;
                  setError('');
                  // Clear parent's clientSecret if callback exists
                  if (onClientSecretChange) {
                    onClientSecretChange(null);
                  }
                }}
              />
              <RadioLabel>
                One-Time Donation
              </RadioLabel>
            </RadioOption>

            <RadioOption 
              className={donationType === 'monthly' ? 'selected' : ''}
                onClick={() => {
                  // Allow switching donation type even when payment element is shown
                  setDonationType('monthly');
                  setPreservedDonationType('monthly');
                  setOneTimeAmount('');
                  setPreservedOneTimeAmount(null);
                  setIsForever(false);
                  setNumberOfPayments('');
                  // Reset payment element when switching types
                  setClientSecret(null);
                  setShowPaymentElement(false);
                  setShowPaymentForm(false);
                  setSubscriptionData(null);
                  setPaymentIntentId(null);
                  paymentIntentCreatedRef.current = false;
                  setError('');
                }}
            >
              <input
                type="radio"
                name="donationType"
                value="monthly"
                checked={donationType === 'monthly'}
                  onChange={() => {
                    // Allow switching donation type even when payment element is shown
                    setDonationType('monthly');
                    setPreservedDonationType('monthly');
                    setOneTimeAmount('');
                    setPreservedOneTimeAmount(null);
                    setIsForever(false);
                    setNumberOfPayments('');
                    // Reset payment element when switching types
                    setClientSecret(null);
                    setShowPaymentElement(false);
                    setShowPaymentForm(false);
                    setSubscriptionData(null);
                    setPaymentIntentId(null);
                    paymentIntentCreatedRef.current = false;
                    setError('');
                  }}
              />
              <RadioLabel>
                Monthly Recurring Donation
              </RadioLabel>
            </RadioOption>
          </RadioGroup>
        </FormSection>

        <FormSection>
          <SectionTitle>
            <FiCreditCard /> Donation Amount
          </SectionTitle>
          
          {donationType === 'one-time' ? (
            <div>
              <Input
                type="number"
                id="oneTimeAmount"
                min="0.01"
                step="0.01"
                placeholder="Enter donation amount (USD)"
                value={oneTimeAmount || (preservedOneTimeAmount ? preservedOneTimeAmount.toString() : '')}
                onChange={(e) => {
                  // Allow changes even when payment element is shown (for editing)
                  const newValue = e.target.value;
                  setOneTimeAmount(newValue);
                  
                  // Clear preserved amount when user manually changes the input
                  // This allows the new value to take precedence and triggers payment intent recreation
                  if (preservedOneTimeAmount) {
                    const newAmount = parseFloat(newValue);
                    const preservedAmount = parseFloat(preservedOneTimeAmount);
                    // Only clear if the value actually changed (more than $0.01 difference)
                    if (isNaN(newAmount) || Math.abs(newAmount - preservedAmount) > 0.01) {
                      setPreservedOneTimeAmount(null);
                      // Clear payment intent so it can be recreated with new amount
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }
                }}
                required
              />
              <QuickShortcuts>
                <ShortcutButton
                  type="button"
                  onClick={() => {
                    const newAmount = 25;
                    const newAmountStr = '25';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $25
                </ShortcutButton>
                <ShortcutButton
                  type="button"
                  onClick={() => {
                    const newAmount = 50;
                    const newAmountStr = '50';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $50
                </ShortcutButton>
                <ShortcutButton
                  type="button"
                  onClick={() => {
                    const newAmount = 100;
                    const newAmountStr = '100';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $100
                </ShortcutButton>
                <ShortcutButton
                  type="button"
                  onClick={() => {
                    const newAmount = 200;
                    const newAmountStr = '200';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $200
                </ShortcutButton>
                <ShortcutButton
                  type="button"
                  onClick={() => {
                    const newAmount = 500;
                    const newAmountStr = '500';
                    // Always set both form value and preserved value
                    setOneTimeAmount(newAmountStr);
                    // Update preserved amount to new value (don't clear it)
                    setPreservedOneTimeAmount(newAmount);
                    // Clear payment intent if amount changed to trigger recreation
                    if (!preservedOneTimeAmount || Math.abs(preservedOneTimeAmount - newAmount) > 0.01) {
                      setClientSecret(null);
                      setShowPaymentElement(false);
                      setShowPaymentForm(false);
                      setPaymentIntentId(null);
                      paymentIntentCreatedRef.current = false;
                      if (onClientSecretChange) {
                        onClientSecretChange(null);
                      }
                    }
                  }}
                >
                  $500
                </ShortcutButton>
                <ShortcutButton
                  type="button"
                  onClick={() => {
                    if (!(showPaymentElement && !!clientSecret)) {
                      setOneTimeAmount('1000');
                    }
                  }}
                  disabled={showPaymentForm && !!clientSecret}
                >
                  $1000
                </ShortcutButton>
              </QuickShortcuts>
            </div>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="monthlyAmount">Monthly Donation Amount (USD) *</Label>
                      <Input
                        type="number"
                        id="monthlyAmount"
                        min="0.01"
                        step="0.01"
                        placeholder="Enter monthly amount (USD)"
                        value={
                          preservedMonthlyAmount 
                            ? preservedMonthlyAmount.toString() 
                            : monthlyAmount
                        }
                        onChange={(e) => {
                          if (!(showPaymentForm && !!clientSecret)) {
                            setMonthlyAmount(e.target.value);
                            // Clear payment intent when amount changes
                            if (clientSecret) {
                              setClientSecret(null);
                              setShowPaymentElement(false);
                              setShowPaymentForm(false);
                              setPaymentIntentId(null);
                              paymentIntentCreatedRef.current = false;
                              if (onClientSecretChange) {
                                onClientSecretChange(null);
                              }
                            }
                          }
                        }}
                        readOnly={showPaymentForm && !!clientSecret}
                        required
                      />
                      <QuickShortcuts>
                        <ShortcutButton
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('25');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $25
                        </ShortcutButton>
                        <ShortcutButton
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('50');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $50
                        </ShortcutButton>
                        <ShortcutButton
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('100');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $100
                        </ShortcutButton>
                        <ShortcutButton
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('200');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $200
                        </ShortcutButton>
                        <ShortcutButton
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('500');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $500
                        </ShortcutButton>
                        <ShortcutButton
                          type="button"
                          onClick={() => {
                            if (!(showPaymentForm && !!clientSecret)) {
                              setMonthlyAmount('1000');
                              // Clear payment intent when amount changes
                              if (clientSecret) {
                                setClientSecret(null);
                                setShowPaymentElement(false);
                                setShowPaymentForm(false);
                                setPaymentIntentId(null);
                                paymentIntentCreatedRef.current = false;
                                if (onClientSecretChange) {
                                  onClientSecretChange(null);
                                }
                              }
                            }
                          }}
                          disabled={showPaymentForm && !!clientSecret}
                        >
                          $1000
                        </ShortcutButton>
                      </QuickShortcuts>
              </div>
                    <div>
                      <Label htmlFor="numberOfPayments">Number of Monthly Payments *</Label>
                      <Input
                        type="number"
                        id="numberOfPayments"
                        min="1"
                        max="120"
                        placeholder={(preservedIsForever || isForever) ? "Unlimited" : "Enter number of payments"}
                        value={
                          (preservedIsForever || isForever)
                            ? '' 
                            : (preservedNumberOfPayments 
                                ? (typeof preservedNumberOfPayments === 'number' 
                                    ? preservedNumberOfPayments.toString() 
                                    : preservedNumberOfPayments === 'forever' 
                                      ? '' 
                                      : preservedNumberOfPayments.toString())
                                : numberOfPayments)
                        }
                        onChange={(e) => {
                          if (!(showPaymentForm && !!clientSecret)) {
                            setNumberOfPayments(e.target.value);
                            setIsForever(false);
                            // Clear payment intent when number of payments changes
                            if (clientSecret) {
                              setClientSecret(null);
                              setShowPaymentElement(false);
                              setShowPaymentForm(false);
                              setPaymentIntentId(null);
                              paymentIntentCreatedRef.current = false;
                              if (onClientSecretChange) {
                                onClientSecretChange(null);
                              }
                            }
                          }
                        }}
                        readOnly={(preservedIsForever || isForever) || (showPaymentForm && !!clientSecret)}
                        required={!(preservedIsForever || isForever)}
                      />
                <QuickShortcuts>
                  <ShortcutButton
                    type="button"
                    onClick={() => {
                      if (!(showPaymentForm && !!clientSecret)) {
                        setNumberOfPayments('6');
                        setIsForever(false);
                        // Clear payment intent when number of payments changes
                        if (clientSecret) {
                          setClientSecret(null);
                          setShowPaymentElement(false);
                          setShowPaymentForm(false);
                          setPaymentIntentId(null);
                          paymentIntentCreatedRef.current = false;
                          if (onClientSecretChange) {
                            onClientSecretChange(null);
                          }
                        }
                      }
                    }}
                    disabled={(preservedIsForever || isForever) || (showPaymentForm && !!clientSecret)}
                    className={
                      (preservedNumberOfPayments === 6 || numberOfPayments === '6') && !(preservedIsForever || isForever) 
                        ? 'selected' 
                        : ''
                    }
                  >
                    6 months
                  </ShortcutButton>
                  <ShortcutButton
                    type="button"
                    onClick={() => {
                      if (!(showPaymentForm && !!clientSecret)) {
                        setNumberOfPayments('12');
                        setIsForever(false);
                        // Clear payment intent when number of payments changes
                        if (clientSecret) {
                          setClientSecret(null);
                          setShowPaymentElement(false);
                          setShowPaymentForm(false);
                          setPaymentIntentId(null);
                          paymentIntentCreatedRef.current = false;
                          if (onClientSecretChange) {
                            onClientSecretChange(null);
                          }
                        }
                      }
                    }}
                    disabled={(preservedIsForever || isForever) || (showPaymentForm && !!clientSecret)}
                    className={
                      (preservedNumberOfPayments === 12 || numberOfPayments === '12') && !(preservedIsForever || isForever) 
                        ? 'selected' 
                        : ''
                    }
                  >
                    12 months
                  </ShortcutButton>
                  <ShortcutButton
                    type="button"
                    onClick={() => {
                      if (!(showPaymentForm && !!clientSecret)) {
                        setNumberOfPayments('24');
                        setIsForever(false);
                        // Clear payment intent when number of payments changes
                        if (clientSecret) {
                          setClientSecret(null);
                          setShowPaymentElement(false);
                          setShowPaymentForm(false);
                          setPaymentIntentId(null);
                          paymentIntentCreatedRef.current = false;
                          if (onClientSecretChange) {
                            onClientSecretChange(null);
                          }
                        }
                      }
                    }}
                    disabled={(preservedIsForever || isForever) || (showPaymentForm && !!clientSecret)}
                    className={
                      (preservedNumberOfPayments === 24 || numberOfPayments === '24') && !(preservedIsForever || isForever) 
                        ? 'selected' 
                        : ''
                    }
                  >
                    24 months
                  </ShortcutButton>
                  <ShortcutButton
                    type="button"
                    onClick={() => {
                      if (!(showPaymentForm && !!clientSecret)) {
                        if (preservedIsForever || isForever) {
                          setIsForever(false);
                          setNumberOfPayments('');
                        } else {
                          setIsForever(true);
                          setNumberOfPayments('forever');
                        }
                        // Clear payment intent when switching between unlimited and limited
                        if (clientSecret) {
                          setClientSecret(null);
                          setShowPaymentElement(false);
                          setShowPaymentForm(false);
                          setPaymentIntentId(null);
                          paymentIntentCreatedRef.current = false;
                          if (onClientSecretChange) {
                            onClientSecretChange(null);
                          }
                        }
                      }
                    }}
                    disabled={showPaymentForm && !!clientSecret}
                    className={(preservedIsForever || isForever) ? 'selected' : ''}
                  >
                    Unlimited
                  </ShortcutButton>
                </QuickShortcuts>
              </div>
            </>
          )}
        </FormSection>

        {!user && (
          <FormSection>
            <SectionTitle>Your Information</SectionTitle>
            
            <div>
              <Label htmlFor="donationFor">Donation For: *</Label>
              <Input
                type="text"
                id="donationFor"
                required
                placeholder="e.g., General Donation/Sadaqah"
                value={donationFor}
                onChange={(e) => setDonationFor(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                type="text"
                id="firstName"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                type="text"
                id="lastName"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </FormSection>
        )}

        {/* Show PaymentElement for:
            1. Old flow: when we have clientSecret (one-time or forever subscriptions)
            2. New flow: when monthly donation is selected with valid amount/payments for PaymentMethod creation
        */}
        {showPaymentForm && (() => {
          // For one-time donations: show if we have clientSecret (from parent or local) and showPaymentElement is true
          // For monthly donations: 
          //   - Forever subscriptions: show if we have clientSecret (created by subscription)
          //   - Limited subscriptions: show if we have valid amount/payments for PaymentMethod creation
          const hasClientSecret = !!(clientSecret || clientSecretFromParent);
          const shouldShowOneTime = showPaymentElement && hasClientSecret && donationType === 'one-time';
          
          // For forever subscriptions, show payment element if we have a clientSecret
          const shouldShowMonthlyForever = donationType === 'monthly' && (isForever || preservedIsForever) && 
            hasClientSecret && showPaymentElement;
          
          // For limited monthly subscriptions, show if we have valid amount/payments
          const shouldShowMonthlyLimited = donationType === 'monthly' && !isForever && !preservedIsForever && 
            (monthlyAmount || preservedMonthlyAmount) && 
            (numberOfPayments || preservedNumberOfPayments) &&
            parseInt(numberOfPayments || preservedNumberOfPayments) >= 1;
          
          const shouldShow = shouldShowOneTime || shouldShowMonthlyForever || shouldShowMonthlyLimited;
          
          return shouldShow;
        })() && (
          <FormSection>
            <SectionTitle>Payment Information</SectionTitle>
            <PaymentElementContainer>
              <PaymentElement 
                options={{
                  // Enable Google Pay and Apple Pay
                  wallets: {
                    googlePay: 'auto',
                    applePay: 'auto'
                  },
                  // Layout options for better UX
                  layout: {
                    type: 'accordion',
                    defaultCollapsed: false
                  }
                }}
              />
            </PaymentElementContainer>
          </FormSection>
        )}

        {((donationType === 'one-time' && (oneTimeAmount || preservedOneTimeAmount)) || (donationType === 'monthly' && (monthlyAmount || preservedMonthlyAmount) && ((numberOfPayments || preservedNumberOfPayments) || isForever || preservedIsForever))) && (
          <SummaryBox>
            {donationType === 'one-time' ? (
              <SummaryTotal>
                <SummaryLabel>Total Amount:</SummaryLabel>
                <SummaryValue>{formatCurrency(preservedOneTimeAmount || totalAmount)}</SummaryValue>
              </SummaryTotal>
            ) : (
              <>
                <SummaryRow>
                  <SummaryLabel>Monthly Amount:</SummaryLabel>
                  <SummaryValue>{formatCurrency(preservedMonthlyAmount || monthlyAmount)}</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>Duration:</SummaryLabel>
                  <SummaryValue>{(preservedIsForever || isForever) ? 'Unlimited' : `${preservedNumberOfPayments || numberOfPayments} months`}</SummaryValue>
                </SummaryRow>
                {!(preservedIsForever || isForever) && (
                  <SummaryTotal>
                    <SummaryLabel>Total Amount:</SummaryLabel>
                    <SummaryValue>{formatCurrency(
                      preservedMonthlyAmount && preservedNumberOfPayments 
                        ? preservedMonthlyAmount * (typeof preservedNumberOfPayments === 'number' ? preservedNumberOfPayments : parseInt(preservedNumberOfPayments))
                        : totalAmount
                    )}</SummaryValue>
                  </SummaryTotal>
                )}
              </>
            )}
          </SummaryBox>
        )}

        {!showPaymentForm ? (
          <SubmitButton
            type="button"
            onClick={handleProceedToPayment}
            disabled={creatingPaymentIntent || loading || 
              (donationType === 'one-time' && (!oneTimeAmount || parseFloat(oneTimeAmount) < 0.01)) ||
              (donationType === 'monthly' && (!monthlyAmount || parseFloat(monthlyAmount) < 0.01 || (!isForever && (!numberOfPayments || numberOfPayments < 1 || numberOfPayments > 120))))}
            style={{ width: '100%' }}
          >
            {creatingPaymentIntent ? (
              <>Creating secure payment...</>
            ) : (
              <>
                <FiCreditCard style={{ marginRight: '0.5rem' }} />
                Proceed to Payment
              </>
            )}
          </SubmitButton>
        ) : (
          <SubmitButton type="submit" disabled={loading || !stripe || (showPaymentElement && !elements)}>
            {loading ? 'Processing...' : showPaymentElement ? 'Complete Payment' : donationType === 'monthly' && isForever ? `Donate ${formatCurrency(monthlyAmount)}/month Unlimited` : totalAmount ? `Donate ${formatCurrency(totalAmount)}` : 'Donate'}
          </SubmitButton>
        )}
      </Form>
    </DonationCard>
  );
}

function DonationForm() {
  const { config, loading, error } = useServerConfig();
  const [clientSecret, setClientSecret] = useState(null);
  const [preservedOneTimeAmount, setPreservedOneTimeAmount] = useState(null); // Preserve amount after payment intent creation
  const [preservedDonationType, setPreservedDonationType] = useState(null); // Preserve donation type to prevent reset on remount
  // Preserve monthly donation values in parent to survive remounts
  const [preservedMonthlyAmount, setPreservedMonthlyAmount] = useState(null);
  const [preservedNumberOfPayments, setPreservedNumberOfPayments] = useState(null);
  const [preservedIsForever, setPreservedIsForever] = useState(false);
  // Preserve subscription data to identify subscription payments after remount
  const [preservedSubscriptionData, setPreservedSubscriptionData] = useState(null);
  
  
  // Use useMemo to ensure Stripe promise is only created once
  // IMPORTANT: Only depend on the stripe key value, not the entire config object
  // This prevents Elements from complaining about stripe prop changes
  const stripeKey = useMemo(() => {
    // Try server config first
    let key = config?.stripePublishableKey;
    
    // Fallback to REACT_APP_STRIPE_PUBLISHABLE_KEY if server config doesn't have it
    if (!key && process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    }
    
    return key;
  }, [config?.stripePublishableKey]);
  
  const stripePromise = useMemo(() => {
    if (stripeKey) {
      return loadStripe(stripeKey);
    }
    return null;
  }, [stripeKey]);

  if (loading) {
    return (
      <Container>
        <DonationCard>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading payment configuration...
          </div>
        </DonationCard>
      </Container>
    );
  }

  if (error || !config || !config.stripePublishableKey) {
    return (
      <Container>
        <DonationCard>
          <ErrorMessage>
            Stripe is not properly configured. Please check your server configuration.
            {error && <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>Error: {error}</div>}
          </ErrorMessage>
        </DonationCard>
      </Container>
    );
  }

  if (!stripePromise) {
    return (
      <Container>
        <DonationCard>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Initializing Stripe...
          </div>
        </DonationCard>
      </Container>
    );
  }

  // Helper function to get computed CSS variable value
  const getCSSVariable = (variableName) => {
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      const value = getComputedStyle(root).getPropertyValue(variableName).trim();
      return value || '#D8B889'; // Fallback to primary color
    }
    // Fallback values for SSR (light theme defaults)
    const fallbacks = {
      '--primary-color': '#D8B889',
      '--background-primary': '#FFFFFF',
      '--text-primary': '#1A202C',
      '--text-secondary': '#4A5568',
      '--error-color': '#E53E3E',
      '--border-color': '#E2E8F0'
    };
    return fallbacks[variableName] || '#D8B889';
  };

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#000000', // Use black instead of primary color for text
      colorText: '#000000', // Force all text to black
      colorTextSecondary: '#000000', // Force secondary text to black
      colorTextPlaceholder: '#999999', // Keep placeholders grey
      colorBackground: getCSSVariable('--background-primary'),
      colorDanger: getCSSVariable('--error-color'),
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      borderRadius: 0
    },
    rules: {
      '.Input': { borderRadius: 0, borderColor: getCSSVariable('--border-color'), color: '#000000' },
      '.Tab': { borderRadius: 0, borderColor: getCSSVariable('--border-color'), color: '#000000' },
      '.Block': { borderRadius: 0, borderColor: getCSSVariable('--border-color') },
      '.Input:focus': { boxShadow: '0 0 0 3px rgba(216, 184, 137, 0.25)' },
      '.Tab:focus': { boxShadow: '0 0 0 3px rgba(216, 184, 137, 0.25)' },
      '.Label': { 
        fontWeight: '600', 
        textTransform: 'uppercase', 
        letterSpacing: '0.025em',
        color: '#000000'
      },
      '.Tab--selected': {
        color: '#000000'
      },
      '.TabIcon': {
        color: '#000000'
      },
      '.TabLabel': {
        color: '#000000'
      },
      '.Text': {
        color: '#000000'
      },
      '.Link': {
        color: '#000000'
      },
      // Note: Stripe Appearance API doesn't support complex selectors with spaces
      // or attribute selectors with quotes. Use simple class selectors only.
    }
  };

  // Determine Elements options - must have either clientSecret or mode
  // For one-time donations, we need to wait for clientSecret before initializing Elements
  // This prevents the "setup mode cannot be confirmed with Payment Intent" error
  
  // Always wrap in Elements (required for hooks), but conditionally provide options
  // Use a stable key when no clientSecret, and clientSecret as key when available to force remount
  // Include paymentMethodCreation: 'manual' to allow using createPaymentMethod with PaymentElement
  const elementsOptions = clientSecret 
    ? { clientSecret, appearance, paymentMethodCreation: 'manual' }
    : { mode: 'setup', currency: 'usd', appearance, paymentMethodCreation: 'manual' }; // Use setup mode initially, but PaymentElement won't render
  
  // Force remount Elements when clientSecret changes from null to a value or vice versa
  const elementsKey = clientSecret ? `donation-${clientSecret.substring(0, 20)}` : 'donation-form-setup';
  
  return (
    <Container>
      <Elements 
        stripe={stripePromise} 
        options={elementsOptions} 
        key={elementsKey}
      >
        <DonationFormContent 
          onClientSecretChange={setClientSecret} 
          clientSecretFromParent={clientSecret}
          preservedOneTimeAmount={preservedOneTimeAmount}
          setPreservedOneTimeAmount={setPreservedOneTimeAmount}
          preservedDonationType={preservedDonationType}
          setPreservedDonationType={setPreservedDonationType}
          preservedMonthlyAmount={preservedMonthlyAmount}
          setPreservedMonthlyAmount={setPreservedMonthlyAmount}
          preservedNumberOfPayments={preservedNumberOfPayments}
          setPreservedNumberOfPayments={setPreservedNumberOfPayments}
          preservedIsForever={preservedIsForever}
          setPreservedIsForever={setPreservedIsForever}
          preservedSubscriptionData={preservedSubscriptionData}
          setPreservedSubscriptionData={setPreservedSubscriptionData}
        />
      </Elements>
    </Container>
  );
}

export default DonationForm;

